import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, ChevronRight } from 'lucide-react';

// Helper function to get the right syntax theme based on current mode
const codeSyntaxTheme = () => {
  const isDarkMode = document.documentElement.classList.contains('dark');
  return isDarkMode ? oneDark : oneLight;
};

const MarkdownRenderer = ({ markdownContent }) => {
  const [toc, setToc] = useState([]);

  // Generate TOC dynamically from headings
  useEffect(() => {
    const headings = [];
    const headingRegex = /^(#{1,4})\s+(.+)$/gm;
    let match;
    while ((match = headingRegex.exec(markdownContent)) !== null) {
      const level = match[1].length;
      const text = match[2].trim();
      const id = text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
      headings.push({ level, text, id });
    }
    setToc(headings);
  }, [markdownContent]);

  return (
    <div className="flex flex-col md:flex-row gap-6 max-w-6xl mx-auto py-8">
      {/* Sticky TOC Sidebar */}
      {toc.length > 0 && (
        <aside className="md:w-1/4 w-full md:sticky md:top-20 md:max-h-[calc(100vh-5rem)] overflow-y-auto scrollbar-thin scrollbar-thumb-blue-400/50 dark:scrollbar-thumb-[#00ff9d]/50 scrollbar-track-gray-200 dark:scrollbar-track-[#131520] p-4 bg-gray-100/95 dark:bg-[#1a1c25]/95 rounded-xl border border-gray-300 dark:border-[#00ff9d]/20 shadow-[0_0_15px_rgba(0,0,0,0.1)] dark:shadow-[0_0_15px_rgba(0,255,157,0.1)]">
          <h2 className="text-xl font-semibold text-blue-600 dark:text-[#00ff9d] mb-4">Table of Contents</h2>
          <ul className="space-y-2">
            {toc.map((item, index) => (
              <li
                key={index}
                className={`pl-${(item.level - 1) * 2} text-gray-700 dark:text-[#e1e7f0]/80 hover:text-blue-600 dark:hover:text-[#00ff9d] transition-colors duration-200`}
              >
                <a
                  href={`#${item.id}`}
                  className="block truncate text-sm"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  {item.text}
                </a>
              </li>
            ))}
          </ul>
        </aside>
      )}

      {/* Main Markdown Content */}
      <div className="md:w-3/4 w-full prose dark:prose-invert max-w-none">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            // Enhanced Headings with IDs for TOC linking
            h1: ({ children }) => (
              <h1
                id={String(children).toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}
                className="text-3xl sm:text-4xl md:text-5xl text-gray-900 dark:text-white font-bold bg-gradient-to-r from-blue-600 via-blue-500 to-blue-700 dark:from-[#00ff9d] dark:via-[#00ccff] dark:to-[#00b8ff] bg-clip-text text-transparent py-3 mb-8 md:mb-10 tracking-tight animate-gradient bg-[length:200%_auto]"
              >
                <span className="relative z-10">{children}</span>
                <div className="h-1 w-full bg-gradient-to-r from-blue-600/20 via-blue-500/20 to-blue-700/20 dark:from-[#00ff9d]/20 dark:via-[#00ccff]/20 dark:to-[#00b8ff]/20 mt-3 rounded-full" />
              </h1>
            ),
            h2: ({ children }) => (
              <h2
                id={String(children).toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}
                className="text-2xl sm:text-3xl font-semibold text-gray-800 dark:text-[#f0f5ff] mt-12 md:mt-16 mb-6 md:mb-8 pb-3 border-b border-blue-500/30 dark:border-[#00ff9d]/30 relative group hover:border-blue-500/50 dark:hover:border-[#00ff9d]/50 transition-colors duration-300"
              >
                <span className="absolute -left-6 md:-left-8 top-0 text-blue-500 dark:text-[#00ff9d] opacity-0 group-hover:opacity-100 transition-opacity duration-300">#</span>
                {children}
              </h2>
            ),
            h3: ({ children }) => (
              <h3
                id={String(children).toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}
                className="text-xl sm:text-2xl font-medium text-gray-800 dark:text-[#d0e0ff] mt-8 md:mt-10 mb-4 md:mb-6 flex items-center gap-2"
              >
                <span className="w-2 h-2 rounded-full bg-blue-500 dark:bg-[#00ff9d] flex-shrink-0" />
                {children}
              </h3>
            ),
            h4: ({ children }) => (
              <h4
                id={String(children).toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}
                className="text-lg sm:text-xl font-medium text-blue-600 dark:text-[#00ff9d] mt-6 md:mt-8 mb-3 md:mb-4 bg-blue-50 dark:bg-[#00ff9d]/10 px-4 py-2 rounded-lg inline-flex items-center gap-2 hover:bg-blue-100 dark:hover:bg-[#00ff9d]/15 transition-colors duration-200"
              >
                <ChevronRight className="h-4 w-4" />
                {children}
              </h4>
            ),

            // Enhanced Code Blocks
            code: ({ className, children, ...props }) => {
              const match = /language-(\w+)/.exec(className || '');
              if (match) {
                return (
                  <div className="relative my-6 md:my-8 group w-full overflow-hidden rounded-xl">
                    <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-2 bg-gray-100 dark:bg-[#131520] border-b border-gray-300 dark:border-[#00ff9d]/20">
                      <span className="text-xs text-blue-600/80 dark:text-[#00ff9d]/80 font-mono">{match[1]}</span>
                      <button
                        className="p-1 text-xs bg-white dark:bg-[#1a1c25] text-blue-600 dark:text-[#00ff9d] rounded border border-gray-300 dark:border-[#00ff9d]/20 opacity-0 group-hover:opacity-100 transition-all duration-200 hover:bg-blue-50 dark:hover:bg-[#00ff9d]/10 flex items-center"
                        onClick={() => navigator.clipboard.writeText(String(children).replace(/\n$/, ''))}
                        title="Copy code"
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Copy
                      </button>
                    </div>
                    <SyntaxHighlighter
                      language={match[1]}
                      style={codeSyntaxTheme()}
                      className="!mt-0 rounded-t-none border border-gray-300 dark:border-[#00ff9d]/20 !bg-gray-50 dark:!bg-[#131520]"
                      showLineNumbers
                      lineNumberStyle={{ color: '#4b5263', minWidth: '2.5em' }}
                      wrapLines={true}
                      wrapLongLines={true}
                      {...props}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  </div>
                );
              }
              return (
                <code className="px-2 py-1 bg-gray-100 dark:bg-[#1a1c25] text-blue-600 dark:text-[#00ff9d] rounded-md border border-gray-300 dark:border-[#00ff9d]/20 text-sm font-mono hover:bg-gray-200 dark:hover:bg-[#1a1c30] transition-colors duration-200">
                  {children}
                </code>
              );
            },

            // Text Elements
            p: ({ children }) => (
              <p className="text-gray-700 dark:text-[#e1e7f0]/90 leading-relaxed mb-5 md:mb-6 text-base md:text-[17px] tracking-wide">
                {children}
              </p>
            ),
            ul: ({ children }) => (
              <ul className="pl-6 mb-6 md:mb-7 space-y-2 md:space-y-3 text-gray-700 dark:text-[#e1e7f0] list-disc list-outside marker:text-blue-500/50 dark:marker:text-[#00ff9d]/50">
                {children}
              </ul>
            ),
            ol: ({ children }) => (
              <ol className="pl-6 mb-6 md:mb-7 space-y-2 md:space-y-3 text-gray-700 dark:text-[#e1e7f0] list-decimal list-outside marker:text-blue-500/50 dark:marker:text-[#00ff9d]/50 marker:font-medium">
                {children}
              </ol>
            ),
            li: ({ children }) => (
              <li className="pl-2 mb-1 marker:hover:text-blue-500 dark:marker:hover:text-[#00ff9d] transition-colors duration-200">
                {children}
              </li>
            ),

            // Interactive Links
            a: ({ children, href }) => (
              <span className="relative text-blue-600 dark:text-[#00ff9d] hover:text-blue-700 dark:hover:text-[#33ffad] transition-colors duration-300 font-medium group">
                <span className="relative z-10">{children}</span>
                <span className="absolute bottom-0 left-0 w-0 h-px bg-blue-600 dark:bg-[#00ff9d] group-hover:w-full transition-all duration-300" />
              </span>
            ),

            // Blockquotes
            blockquote: ({ children }) => (
              <blockquote className="border-l-4 border-blue-500 dark:border-[#00ff9d] pl-4 md:pl-6 py-3 my-6 md:my-8 bg-gradient-to-r from-blue-50 dark:from-[#00ff9d]/5 to-transparent rounded-r-lg text-gray-700 dark:text-[#e6edf3] italic relative hover:border-blue-600 dark:hover:border-[#00ff9d] transition-colors duration-300">
                <span className="absolute -left-2 top-0 text-4xl text-blue-500 dark:text-[#00ff9d] opacity-30">"</span>
                <div className="ml-4 relative z-10">{children}</div>
              </blockquote>
            ),

            // Tables
            table: ({ children }) => (
              <div className="my-8 md:my-10 overflow-hidden rounded-2xl border border-gray-300 dark:border-[#00ff9d]/20 shadow-xl backdrop-blur-sm hover:shadow-[0_0_20px_-5px_rgba(0,0,0,0.2)] dark:hover:shadow-[0_0_20px_-5px_rgba(0,255,157,0.2)] transition-shadow duration-300">
                <table className="w-full divide-y divide-gray-300 dark:divide-[#00ff9d]/10">{children}</table>
              </div>
            ),
            thead: ({ children }) => (
              <thead className="bg-gradient-to-r from-blue-50 to-blue-100/50 dark:from-[#00ff9d]/10 dark:to-[#00b8ff]/10">{children}</thead>
            ),
            tbody: ({ children }) => (
              <tbody className="divide-y divide-gray-200 dark:divide-[#00ff9d]/10 bg-white dark:bg-[#131520]/90">{children}</tbody>
            ),
            tr: ({ children }) => (
              <tr className="hover:bg-blue-50 dark:hover:bg-[#00ff9d]/5 transition-colors duration-200">{children}</tr>
            ),
            th: ({ children }) => (
              <th className="px-4 md:px-6 py-3 md:py-4 text-left text-sm font-semibold text-blue-700 dark:text-[#00ff9d] uppercase tracking-wider">
                {children}
              </th>
            ),
            td: ({ children }) => (
              <td className="px-4 md:px-6 py-3 md:py-4 text-sm md:text-base text-gray-700 dark:text-[#e1e7f0]/90">
                {children}
              </td>
            ),

            hr: () => (
              <div className="relative">
                <hr className="my-8 md:my-12 border-t border-gray-300 dark:border-[#00ff9d]/20 hover:border-blue-500 dark:hover:border-[#00ff9d] transition-colors duration-300" />
                <span className="absolute left-1/2 -translate-x-1/2 -top-3 px-3 bg-white dark:bg-[#131520] text-blue-500 dark:text-[#00ff9d] text-sm rounded-full border border-gray-300 dark:border-[#00ff9d]/20 hover:bg-blue-50 dark:hover:bg-[#00ff9d]/10 hover:text-blue-600 dark:hover:text-[#00ff9d] transition-colors duration-300">
                  ✧
                </span>
              </div>
            ),

            // Images
            img: ({ src, alt }) => (
              <div className="my-8 md:my-10 group relative overflow-hidden rounded-2xl border-2 border-gray-300 dark:border-[#00ff9d]/20 shadow-xl hover:shadow-[0_10px_30px_-5px_rgba(59,130,246,0.3)] dark:hover:shadow-[0_10px_30px_-5px_rgba(0,255,157,0.3)] transition-all duration-500">
                <div className="aspect-video w-full bg-gray-100 dark:bg-[#1a1c23] relative overflow-hidden">
                  <img
                    src={src}
                    alt={alt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-blue-500/5 dark:bg-[#00ff9d]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                {alt && (
                  <p className="mt-3 text-center text-sm text-gray-500 dark:text-[#e1e7f0]/80 italic animate-pulse px-4">
                    {alt}
                  </p>
                )}
              </div>
            ),
          }}
        >
          {markdownContent}
        </ReactMarkdown>
      </div>
    </div>
  );
};

export default MarkdownRenderer;

// Usage Example
// <MarkdownRenderer markdownContent={state.documentation} />